/**
 * Экран режимов «Цифры», «Пунктуация» и «Специальные символы».
 *
 * Пользователю показывается, что он осваивает сейчас и — если символ
 * набирается с Shift — какую клавишу этот Shift меняет: искать такой символ
 * глазами по всей клавиатуре бессмысленно (ТЗ §6, режимы 8–10).
 *
 * Обе эти вещи нужны во время набора, поэтому стоят над текстом. Список всех
 * открытых символов и объяснение режима — под ним (ТЗ FR-EN-84).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card } from '@type2x/ui';
import type { SymbolsPlan } from '@type2x/feature-symbols';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface SymbolsScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly plan: SymbolsPlan;
  readonly sessionId: SessionId;
  readonly onFinished: (run: TypingSession) => void;
}

export function SymbolsScreen({
  runtime,
  t,
  plan,
  sessionId,
  onFinished,
}: SymbolsScreenProps): React.JSX.Element {
  const title = t(`symbols.title.${plan.mode}`);
  if (plan.group === null) return <Card title={title}>{t('symbols.done')}</Card>;

  const total = plan.totalGroups;

  return (
    <DrillLayout
      t={t}
      title={title}
      now={
        <>
          <span className="t2x-muted">
            {t('symbols.group', { characters: plan.group.characters.join(' ') })}
          </span>
          {/* Подсказка о Shift нужна ровно в тот момент, когда символ нужно
              набрать, — значит, на виду, а не под раскрытием. */}
          {plan.shiftHints.length > 0 && (
            <ul className="t2x-shift-hints" aria-label={t('symbols.shift')}>
              {plan.shiftHints.map((hint) => (
                <li key={hint.character}>
                  {t('symbols.shiftHint', { character: hint.character, base: hint.base })}
                </li>
              ))}
            </ul>
          )}
        </>
      }
      progress={{
        value: total === 0 ? 0 : plan.completedGroupIds.length / total,
        label: t('symbols.progress'),
      }}
      details={
        <>
          {/* Подпись видимая, а не только для чтения с экрана: крупная строка
              без неё читается как задание, хотя осваивается лишь одна группа —
              та, что названа выше (ТЗ FR-EN-65). */}
          <p className="t2x-muted">
            {t('symbols.unlockedCount', {
              open: plan.unlockedCharacters.length,
              total: plan.totalCharacters,
              keys: plan.unlockedCharacters.join(' '),
            })}
          </p>

          <p className="t2x-muted">{t(`symbols.hint.${plan.mode}`)}</p>
        </>
      }
    >
      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={t('symbols.typing')}
        hint={t(`symbols.hint.${plan.mode}`)}
        sessionId={sessionId}
        layoutId={plan.layoutId}
        targetText={plan.exercise.text}
        hintLevel={plan.hintLevel}
        compact
        onFinished={onFinished}
      />
    </DrillLayout>
  );
}
